
import React from 'react';
import { BookOpen, Wrench, Zap, ArrowRight } from 'lucide-react';

export const CompetenceSection: React.FC = () => {
  return (
    <section className="py-24 px-4 bg-slate-950 relative overflow-hidden border-t border-white/5">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative z-10">
        {/* Venn Diagram Column */}
        <div className="w-full lg:w-1/2 flex justify-center items-center order-2 lg:order-1">
          <div className="relative w-full max-w-[500px] aspect-square flex items-center justify-center">
            {/* Contexto Circle (Outer) */}
            <div className="absolute inset-0 border-2 border-dashed border-slate-800 rounded-full flex items-start justify-center pt-6">
              <span className="text-slate-600 font-bold uppercase tracking-[0.3em] text-[10px]">Contexto</span>
            </div>
            
            {/* Venn Circles SVG */}
            <svg viewBox="0 0 400 400" className="w-[85%] h-[85%] drop-shadow-[0_0_30px_rgba(79,70,229,0.2)]">
              <defs>
                <clipPath id="clip-conhecimento">
                  <circle cx="200" cy="150" r="95" />
                </clipPath>
                <clipPath id="clip-habilidade">
                  <circle cx="145" cy="245" r="95" />
                </clipPath>
                <clipPath id="clip-atitude">
                  <circle cx="255" cy="245" r="95" />
                </clipPath>
                <clipPath id="clip-center">
                  <circle cx="200" cy="150" r="95" clipPath="url(#clip-habilidade)" />
                </clipPath>
              </defs>

              {/* Main Circles - Increased brightness from /30 to /50 */}
              <circle cx="200" cy="150" r="95" className="fill-blue-600/50 stroke-blue-500/60 stroke-[1.5]" />
              <circle cx="145" cy="245" r="95" className="fill-yellow-500/50 stroke-yellow-500/60 stroke-[1.5]" />
              <circle cx="255" cy="245" r="95" className="fill-red-600/50 stroke-red-500/60 stroke-[1.5]" />
              
              {/* Intersections */}
              {/* Blue + Yellow = Green */}
              <circle cx="145" cy="245" r="95" className="fill-emerald-500/80" clipPath="url(#clip-conhecimento)" />
              {/* Blue + Red = Purple */}
              <circle cx="255" cy="245" r="95" className="fill-purple-500/80" clipPath="url(#clip-conhecimento)" />
              {/* Yellow + Red = Orange */}
              <circle cx="255" cy="245" r="95" className="fill-orange-500/80" clipPath="url(#clip-habilidade)" />
              
              {/* Center Intersection (Competência) */}
              <circle cx="255" cy="245" r="95" className="fill-indigo-400" clipPath="url(#clip-center)" />

              {/* Labels for main circles */}
              <text x="200" y="105" textAnchor="middle" className="fill-white font-bold text-[13px] tracking-tight">Conhecimento</text>
              <text x="110" y="285" textAnchor="middle" className="fill-white font-bold text-[13px] tracking-tight">Habilidade</text>
              <text x="290" y="285" textAnchor="middle" className="fill-white font-bold text-[13px] tracking-tight">Atitude</text>
              
              {/* Intersections: Saber (Increased brightness to white) */}
              <text x="145" y="190" textAnchor="middle" className="fill-white font-bold text-[9px] uppercase tracking-wider drop-shadow-sm">Saber</text>
              <text x="255" y="190" textAnchor="middle" className="fill-white font-bold text-[9px] uppercase tracking-wider drop-shadow-sm">Saber</text>
              <text x="200" y="285" textAnchor="middle" className="fill-white font-bold text-[9px] uppercase tracking-wider drop-shadow-sm">Saber</text>
              
              {/* Center: Competência Label */}
              <g className="filter drop-shadow-md">
                <rect x="150" y="210" width="100" height="24" rx="12" className="fill-indigo-600" />
                <text x="200" y="226" textAnchor="middle" className="fill-white font-black text-[10px] uppercase tracking-[0.15em]">Competência</text>
              </g>
            </svg>
          </div>
        </div>

        {/* Text Column */}
        <div className="w-full lg:w-1/2 order-1 lg:order-2">
          <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-8 leading-tight">
            Desenvolvendo competências <br />
            <span className="gradient-text">em sua totalidade</span>
          </h2>
          
          <div className="space-y-6 text-slate-400 leading-relaxed">
            <p className="text-lg">
              Na Qualle, acreditamos que o aprendizado vai além da sala de aula. Para que você realmente se destaque, é necessário o desenvolvimento de competências sólidas, formadas por três pilares fundamentais:
            </p>
            
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                  <BookOpen size={20} />
                </div>
                <div>
                  <h4 className="text-white font-bold">Conhecimento</h4>
                  <p className="text-sm">É o domínio teórico, a compreensão dos conceitos e a base intelectual necessária para qualquer desafio.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-400">
                  <Wrench size={20} />
                </div>
                <div>
                  <h4 className="text-white font-bold">Habilidade</h4>
                  <p className="text-sm">É a capacidade de colocar o conhecimento em prática, transformando teoria em resultados concretos e eficientes.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                  <Zap size={20} />
                </div>
                <div>
                  <h4 className="text-white font-bold">Atitude</h4>
                  <p className="text-sm">É o motor comportamental — a motivação, a ética e a proatividade que determinam como você utiliza suas habilidades.</p>
                </div>
              </div>
            </div>
            
            <p className="border-l-2 border-indigo-500 pl-6 py-2 italic bg-indigo-500/5 rounded-r-2xl">
              Para que uma competência seja plenamente desenvolvida, você deve trabalhar esses três atributos de forma integrada. Nossa plataforma foi desenhada para identificar exatamente onde você precisa focar, ajudando-o a construir a competência necessária para atingir seus objetivos mais ambiciosos ou solucionar os problemas mais complexos do seu dia a dia.
            </p>
            
            <div className="pt-4">
              <a 
                href="#waitlist-section" 
                className="inline-flex items-center justify-center px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-600/20 active:scale-95 group"
              >
                Quero fazer parte
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
